// eslint-disable-next-line no-restricted-imports
import { useTheme } from '@emotion/react';
import type {
  BrandSpecial,
  StoreSpecial as V3StoreSpecial,
} from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import {
  useBrandSpecialQualifyingStatus,
  useGetSpecialQualifyingStatus,
  useStoreSpecials,
  useAddCartProduct as useV3AddCartProduct,
  useMaterializedMenuProduct,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  getMenuPdpApplicableGoldTagLabel,
  getPriceIdsForBrandSpecial,
  getPriceIdsForStoreSpecial,
  useNavigateBack,
} from '@iheartjane/menu';
import {
  Box,
  Button,
  Flex,
  Loading,
  Typography,
  contrastingColor,
  useToast,
} from '@iheartjane/reefer';
import type { Nullable, PriceId } from '@iheartjane/types';
import { isPriceId } from '@iheartjane/types';
import { useCallback, useMemo, useState } from 'react';
import {
  useLocation,
  useMatches,
  useParams,
  useSearchParams,
} from 'react-router';

import {
  Modal,
  ProductAddedToast,
  ProductRow,
  ProductWeightsGrid,
} from '@jane/kiosk/components';
import { maxQuantityForProduct } from '@jane/kiosk/components/lib/ProductRow/cartRowProduct';
import type {
  ProductDetailLoaderType,
  RootLoaderType,
} from '@jane/kiosk/data-access';
import { useLocalCartUuid } from '@jane/kiosk/data-access';
import {
  useAddProductOnSuccess,
  useAddProductToBag,
  useGetProductPrices,
  useKioskCart,
  useKioskSpacing,
  useRowContext,
  useShouldKioskShowGold,
  useTypedLoaderData,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import { useBrandSpecials, useProductTracking } from '@jane/kiosk/providers';
import type { OptionalMenuRouteParams } from '@jane/kiosk/routing/types';
import { getSource, isSupportedStoreSpecial } from '@jane/kiosk/util';
import { AVAILABLE_WEIGHTS } from '@jane/search/util';
import {
  useAddCartProduct,
  useGetStoreSpecials,
} from '@jane/shared-ecomm/data-access';
import { useGetStore } from '@jane/shared/data-access';
import type { Store, StoreSpecial } from '@jane/shared/types';
import {
  availableWeightsForMenuProduct,
  calculateDiscountPercentage,
  getMenuRowWeightFilter,
  getWeightFilterForFeaturedCategory,
  lowestPriceForMenuProduct,
} from '@jane/shared/util';

const useProductDetailsLoaderData = () => {
  const routeSpecificData =
    useTypedRouteLoaderData<ProductDetailLoaderType>('cartWeightModal');
  const loaderData = useTypedLoaderData<ProductDetailLoaderType>();

  return routeSpecificData === undefined ? loaderData : routeSpecificData;
};

export const AddMultiWeightPrompt = () => {
  const {
    colors: {
      primary: { main },
    },
  } = useTheme();
  const { storeId, store: initialStore } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { product: initialProduct, productId } = useProductDetailsLoaderData();
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const multipleConditions = useFlag(FLAGS.c3MultipleConditionSpecials);
  const cartUuid = useLocalCartUuid();
  const { data: cart } = useKioskCart();
  const [queryParams] = useSearchParams();
  const {
    cartToppers: { addCartTopperId },
    inlineAds: { addInlineAdId },
  } = useProductTracking();
  const { brandSpecials } = useBrandSpecials();

  const { specialId, brandSpecialId } = useParams<OptionalMenuRouteParams>();
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const supportedStoreSpecials = specials.filter((special) =>
    isSupportedStoreSpecial(special, true)
  );

  const specialInScope = supportedStoreSpecials.find(
    ({ id }) => `${id}` === specialId
  );
  const { pathname } = useLocation();
  const matches = useMatches();

  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });

  const { rowId, featuredCategory } = useRowContext(store);
  const menuRowWeightFilter = useMemo(
    () =>
      rowId
        ? getMenuRowWeightFilter({
            customRowSettings: store.custom_row_settings,
            rowId,
          })
        : getWeightFilterForFeaturedCategory(store, featuredCategory),
    [store, featuredCategory, rowId]
  );

  const { data: product, isPending: isProductLoading } =
    useMaterializedMenuProduct(
      {
        productId,
        storeId,
      },
      { initialData: initialProduct }
    );
  const getProductPrice = useGetProductPrices(product, specials);

  const weights = availableWeightsForMenuProduct(product);

  const brandSpecial = brandSpecials.find(
    ({ id }) => id === Number(brandSpecialId)
  );
  const { data: qualifyingStatusData } = useBrandSpecialQualifyingStatus({
    cartUuid: cartUuid ?? undefined,
    specialId: brandSpecial?.id,
    storeId: store?.id,
  });

  const { data: storeSpecialQualifyingStatusData } =
    useGetSpecialQualifyingStatus({
      cartUuid: cartUuid ?? undefined,
      specialId: specialInScope?.id,
      storeId: store?.id,
    });

  const applicablePriceIds = specialInScope
    ? getPriceIdsForStoreSpecial({
        multipleConditions,
        product,
        qualifyingStatus:
          storeSpecialQualifyingStatusData?.qualifying_status ??
          'not_qualified',
        special: specialInScope,
      })
    : getPriceIdsForBrandSpecial(
        brandSpecial,
        qualifyingStatusData?.qualifying_status ?? 'not_qualified'
      );

  // if there are no weight restrictions, we return all of the weights
  const visiblePriceIds =
    applicablePriceIds.length > 0 &&
    applicablePriceIds.length !== AVAILABLE_WEIGHTS.length
      ? applicablePriceIds
      : null;

  const priceId = useMemo(() => {
    const priceParam = queryParams.get('priceId');
    if (isPriceId(priceParam)) {
      return weights.includes(priceParam) ? priceParam : null;
    }
    return null;
  }, [queryParams, weights]);

  const isInlineAd = useMemo(
    () => queryParams.get('inline')?.toLowerCase() === 'true',
    [queryParams]
  );

  const visibleWeights = availableWeightsForMenuProduct(
    product,
    visiblePriceIds
  );
  const selectableWeights = visibleWeights.length ? visibleWeights : weights;

  const [chosenWeight, setChosenWeight] = useState<Nullable<PriceId>>(priceId);

  // Specials and their qualifying status load after mount and can hide the
  // chosen weight, so fall back to the cheapest weight still shown
  const selectedWeight =
    chosenWeight && selectableWeights.includes(chosenWeight)
      ? chosenWeight
      : lowestPriceForMenuProduct(product, selectableWeights) || 'each';

  const { unitPrice, discountPrice } = getProductPrice(selectedWeight);
  const discountPercentage = useMemo(() => {
    return calculateDiscountPercentage(unitPrice, discountPrice);
  }, [unitPrice, discountPrice]);

  const shouldShowGold = useShouldKioskShowGold();

  const brandSpecialsForMenuProduct: BrandSpecial[] = useMemo(
    () =>
      (product.applicable_brand_special_ids ?? []).flatMap((id) => {
        const special = brandSpecials.find((special) => special.id === id);
        return special ? [special] : [];
      }),
    [product.applicable_brand_special_ids, brandSpecials]
  );

  const janeGoldTagLabel = useMemo(() => {
    if (!shouldShowGold) return null;

    return getMenuPdpApplicableGoldTagLabel({
      brandSpecials: brandSpecialsForMenuProduct,
      menuProduct: product,
      selectedWeight,
    });
  }, [brandSpecialsForMenuProduct, product, selectedWeight, shouldShowGold]);

  const { getKioskSpacing, isIpad } = useKioskSpacing();
  const goBack = useNavigateBack(`stores/${storeId}/menu`);

  const { mutate: addCartProduct, isPending: isAddingTopper } =
    useAddCartProduct({
      onSuccess: () => {
        addCartTopperId(product.product_id);
      },
    });
  const { mutate: v3AddCartProduct, isPending: isAddingTopperV3 } =
    useV3AddCartProduct({
      onSuccess: () => {
        addCartTopperId(product.product_id);
      },
    });

  const pdpDepth = queryParams.get('pdpDepth');

  const trackDmProducts = useCallback(() => {
    if (isInlineAd) {
      addInlineAdId(product.product_id);
    }
    goBack(Number(pdpDepth) || 1);
  }, [isInlineAd, pdpDepth]);

  const onSuccess = useAddProductOnSuccess({
    callback: trackDmProducts,
    cart,
    discountPercentage,
    discountPrice,
    product,
    selectedWeight,
    source: getSource({ pathname, matches, isTopper: false }),
    store,
    unitPrice,
    quickAdd: true,
    isInlineAd,
    specialInScope,
  });

  const { onAddToBag, isLoading: isAddingMenu } = useAddProductToBag({
    cart,
    priceId: selectedWeight,
    product,
    onSuccess,
    store,
  });

  const isAdding = useMemo(
    () => (useV3Cart ? isAddingTopperV3 : isAddingTopper) || isAddingMenu,
    [useV3Cart, isAddingTopperV3, isAddingMenu, isAddingTopper]
  );

  const loadingColor = useMemo(() => {
    const color = contrastingColor(main, ['#0E0E0E', '#FFFFFF']);
    return color === '#FFFFFF' ? 'white' : 'black';
  }, [main]);
  const toast = useToast();

  const onAddCartTopper = useCallback(
    (weight: PriceId = selectedWeight) => {
      if (useV3Cart) {
        v3AddCartProduct({
          appMode: 'kiosk',
          cartUuid: cart.uuid,
          storeId,
          product: {
            count: 1,
            price_id: weight,
            product_id: product.product_id,
            recommended: false,
          },
        });
      } else {
        addCartProduct({
          app_mode: 'kiosk',
          app_mode_name: 'kiosk',
          cart_uuid: cart.uuid,
          product: {
            count: 1,
            price_id: weight,
            product_id: product.product_id,
            recommended: false,
          },
          store_id: storeId,
          user_agent: window.navigator.userAgent,
        });
      }
      toast.add({
        label: <ProductAddedToast productCount={1} storeId={storeId} />,
      });
      goBack(Number(pdpDepth) || 1);
    },
    [
      useV3Cart,
      v3AddCartProduct,
      addCartProduct,
      cart.uuid,
      goBack,
      product.product_id,
      selectedWeight,
      storeId,
      toast,
      pdpDepth,
    ]
  );
  const addToBag = useCallback(
    (weight: PriceId = selectedWeight) => {
      pathname.includes('cart')
        ? onAddCartTopper(weight)
        : onAddToBag({ weight });
    },
    [onAddCartTopper, onAddToBag, pathname, selectedWeight]
  );

  const onWeightClick = useCallback((weight: PriceId) => {
    setChosenWeight(weight);
  }, []);

  const disableAdd = useMemo(() => {
    const existingCartProduct = (cart?.products ?? []).find(
      ({ price_id, product_id }) =>
        product.product_id === product_id && price_id === selectedWeight
    );
    return (
      isAdding ||
      (!!existingCartProduct &&
        existingCartProduct.count ===
          maxQuantityForProduct(existingCartProduct))
    );
  }, [cart, isAdding, product.product_id, selectedWeight]);

  return (
    <Modal
      appId="root"
      contentLabel="Add Topper"
      onRequestClose={() => goBack()}
      open
      overflow="visible"
      variant="dialogue"
    >
      <Modal.Content overflowY="auto" padding={false}>
        <Flex
          flexDirection="column"
          p={getKioskSpacing(32, 40)}
          minWidth={isIpad ? '61vw' : '48vw'}
        >
          <Typography mb={getKioskSpacing(24, 8)} variant="h2-semibold">
            Select purchase option
          </Typography>
          <Box
            border="transparent-black-five"
            borderRadius="sm"
            mb={24}
            pt={24}
            px={24}
          >
            <ProductRow
              janeGoldTagLabel={janeGoldTagLabel}
              product={product}
              showPrice={false}
            />
          </Box>

          <Typography mb={16} variant="body-semibold">
            Select weight
          </Typography>

          <ProductWeightsGrid
            numColumns={2}
            onWeightClick={onWeightClick}
            product={product}
            selectedWeight={selectedWeight}
            shouldShowGold={shouldShowGold}
            specials={specials}
            visiblePrideIds={visiblePriceIds ?? undefined}
            weightFilter={menuRowWeightFilter}
            weightRuleBrandSpecial={
              !janeGoldTagLabel ? brandSpecialsForMenuProduct : []
            }
          />
          <Flex flexDirection="row" mt={32}>
            <Button
              ariaLabel="cancel"
              full
              loading={isProductLoading}
              label={
                <Typography
                  color="text-main"
                  variant={isIpad ? 'body-semibold' : 'h4-semibold'}
                >
                  Cancel
                </Typography>
              }
              mr={getKioskSpacing(24, 16)}
              onClick={() => goBack()}
              variant="tertiary"
              size={isIpad ? 'small' : 'large'}
            />
            <Button
              ariaLabel="add product to bag"
              full
              disabled={disableAdd}
              label={
                isAdding ? (
                  <Loading color={loadingColor} size="sm" />
                ) : (
                  <Typography
                    color="text-info"
                    variant={isIpad ? 'body-semibold' : 'h4-semibold'}
                  >
                    Add to bag
                  </Typography>
                )
              }
              onClick={() => addToBag()}
              size={isIpad ? 'small' : 'large'}
            />
          </Flex>
        </Flex>
      </Modal.Content>
    </Modal>
  );
};
